import { io } from 'socket.io-client';
import { axiosPrivate } from '../api/axios';
import { bindSocketListener } from './socketListeners';
import { SOCKET_URL } from '../config/env';
import { getPresenceSessionId } from './presenceSession';
import { getDeviceInfo } from './deviceInfo';

let socket = null;
let connectPromise = null;
let connectionLifecycleAttached = false;
let authRetryInFlight = false;
let pageHideAttached = false;

const buildSocketAuth = (token) => ({
    token,
    platform: 'web',
    sessionId: getPresenceSessionId(),
    device: getDeviceInfo(),
});

const fetchSocketAuthToken = async () => {
    const { data } = await axiosPrivate.get('/api/chat-realtime/socket-auth');
    return data.token;
};

const refreshSocketAuth = async () => {
    const token = await fetchSocketAuthToken();
    if (socket) {
        socket.auth = buildSocketAuth(token);
    }
    return token;
};

const emitSessionUnload = (event) => {
    if (event?.persisted) return;
    if (!socket?.connected) return;
    socket.emit('usuarios_online_unload');
};

const attachPageHideListener = () => {
    if (pageHideAttached || typeof window === 'undefined') return;
    pageHideAttached = true;
    window.addEventListener('pagehide', emitSessionUnload);
    window.addEventListener('beforeunload', emitSessionUnload);
};

const attachConnectionLifecycle = (s) => {
    if (connectionLifecycleAttached) return;
    connectionLifecycleAttached = true;
    attachPageHideListener();

    s.on('connect_error', async () => {
        if (authRetryInFlight || s.connected) return;

        authRetryInFlight = true;
        try {
            await refreshSocketAuth();
            s.connect();
        } catch {
            // La sesión HTTP expiró; axiosPrivate redirige al login si aplica.
        } finally {
            authRetryInFlight = false;
        }
    });
};

const createSocket = (token) => {
    socket = io(SOCKET_URL, {
        withCredentials: true,
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelayMax: 10000,
        auth: buildSocketAuth(token),
    });

    attachConnectionLifecycle(socket);
    socket.on('presence_probe', (_payload, cb) => {
        if (typeof cb === 'function') cb({ ok: true });
    });

    return socket;
};

export const connectSocket = async () => {
    if (socket?.connected) return socket;

    if (connectPromise) return connectPromise;

    connectPromise = (async () => {
        try {
            const token = await fetchSocketAuthToken();

            if (socket) {
                socket.auth = buildSocketAuth(token);
                attachConnectionLifecycle(socket);
                if (!socket.connected) {
                    socket.connect();
                }
                return socket;
            }

            return createSocket(token);
        } finally {
            connectPromise = null;
        }
    })();

    return connectPromise;
};

/**
 * Reconecta tras idle/background: refresca el JWT, fuerza connect
 * y espera a que el socket quede realmente conectado.
 */
export const ensureSocketConnected = async (timeoutMs = 10000) => {
    if (socket?.connected) return socket;

    if (connectPromise) {
        await connectPromise;
        if (socket?.connected) return socket;
    }

    const token = await fetchSocketAuthToken();

    if (!socket) {
        createSocket(token);
    } else {
        socket.auth = buildSocketAuth(token);
        attachConnectionLifecycle(socket);
        if (!socket.connected) {
            socket.connect();
        }
    }

    if (socket.connected) return socket;

    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            cleanup();
            if (socket?.connected) {
                resolve(socket);
                return;
            }
            reject(new Error('No se pudo reconectar al chat en tiempo real'));
        }, timeoutMs);

        const onConnect = () => {
            cleanup();
            resolve(socket);
        };

        const cleanup = () => {
            clearTimeout(timer);
            try {
                socket?.off?.('connect', onConnect);
            } catch {
                // socket ya cerrado
            }
        };

        socket.once('connect', onConnect);
        if (!socket.connected) {
            socket.connect();
        }
    });
};

export const disconnectSocket = () => {
    if (socket) {
        socket.disconnect();
        socket.removeAllListeners();
        socket = null;
    }
    connectPromise = null;
    connectionLifecycleAttached = false;
    authRetryInFlight = false;
};

export const getSocket = () => socket;

export const isSocketConnected = () => Boolean(socket?.connected);

export const waitForSocketConnection = (timeoutMs = 8000) => new Promise((resolve, reject) => {
    connectSocket()
        .then((s) => {
            if (s.connected) {
                resolve(s);
                return;
            }

            const detach = (event, handler) => {
                try {
                    s?.off?.(event, handler);
                } catch {
                    // socket ya cerrado
                }
            };

            const timer = setTimeout(() => {
                detach('connect', onConnect);
                detach('connect_error', onError);
                reject(new Error('No se pudo conectar al chat en tiempo real'));
            }, timeoutMs);

            const onConnect = () => {
                clearTimeout(timer);
                detach('connect_error', onError);
                resolve(s);
            };

            const onError = (err) => {
                clearTimeout(timer);
                detach('connect', onConnect);
                reject(new Error(err?.message || 'Error de autenticación en el chat'));
            };

            s.once('connect', onConnect);
            s.once('connect_error', onError);

            if (!s.connected) {
                s.connect();
            }
        })
        .catch(reject);
});

export const joinCourseRoom = (courseId) => {
    if (!socket?.connected) return null;
    socket.emit('join_room', { courseId });
    return socket;
};

export const leaveCourseRoom = (courseId) => {
    if (socket?.connected) {
        socket.emit('leave_room', { courseId });
    }
};

export const joinUsuariosOnlineRoom = () => {
    if (!socket?.connected) return null;
    socket.emit('usuarios_online_join');
    return socket;
};

export const leaveUsuariosOnlineRoom = () => {
    if (socket?.connected) {
        socket.emit('usuarios_online_leave');
    }
};

export const sendSocketMessage = (payload) => new Promise((resolve, reject) => {
    waitForSocketConnection()
        .then((s) => {
            const timer = setTimeout(() => {
                reject(new Error('Tiempo de espera agotado al enviar mensaje'));
            }, 8000);

            s.emit('send_message', payload, (response) => {
                clearTimeout(timer);
                if (!response) {
                    reject(new Error('Sin respuesta del servidor'));
                    return;
                }
                if (response.error) {
                    reject(new Error(response.error));
                    return;
                }
                resolve(response);
            });
        })
        .catch(reject);
});

export const emitTypingStart = (courseId, conversationId) => {
    if (!socket?.connected) return;
    socket.emit('typing_start', { courseId, conversationId });
};

export const emitTypingStop = (courseId, conversationId) => {
    if (!socket?.connected) return;
    socket.emit('typing_stop', { courseId, conversationId });
};

export const emitReadReceipt = (courseId, conversationId, messageId) => {
    if (!socket?.connected) return;
    socket.emit('read_receipt', { courseId, conversationId, messageId });
};

export const emitZoomParticipantJoin = (courseId, sessionId) => {
    if (!socket?.connected || !courseId || !sessionId) return;
    socket.emit('zoom_participant_join', { courseId, sessionId });
};

/**
 * `reason: 'self'` = el usuario salió por su cuenta (único caso que se avisa al resto);
 * `'ended'` = la llamada se cerró por el profesor, por desmontaje o por la sesión.
 */
export const emitZoomParticipantLeave = (courseId, sessionId, reason = 'ended') => {
    if (!socket?.connected || !courseId || !sessionId) return;
    socket.emit('zoom_participant_leave', { courseId, sessionId, reason });
};

export const onSocketEvent = (event, handler) => bindSocketListener(socket, event, handler);

export const onSocketConnect = (handler) => onSocketEvent('connect', handler);
export const onSocketDisconnect = (handler) => onSocketEvent('disconnect', handler);
export const onSocketConnectError = (handler) => onSocketEvent('connect_error', handler);
